import { useMemo } from "react";

import ProjectGridView from "components/hosted-scraping/ProjectGridView";
import ProjectListView from "components/hosted-scraping/ProjectListView";
import { PlusCircleIcon } from "@heroicons/react/outline";

import { useHostedScrapingProjects } from "providers/HostedScrapingProvider";
import { PROJECT_PAGE_SIZE } from "providers/HostedScrapingProvider/ProjectProvider";
import { ReactComponent as ProjectGridIcon } from "assets/icons/project-grid.svg";
import { ReactComponent as ProjectListIcon } from "assets/icons/project-list.svg";
import EmptyProjectListView from "components/hosted-scraping/EmptyProjectListView";
import EmptyProjectGridView from "components/hosted-scraping/EmptyProjectGridView";
import { useLocalStorage } from "hooks/useLocalStorage";
import { FullWidthLoadingSpinner } from "components/FullWidthLoadingSpinner";
import BorderedPage from "components/BorderedPage";
import { LabelledSelect } from "components/LabelledSelect";
import { SelectOption } from "components/Select";
import ScrollToHeaderTitle from "misc/ScrollToHeaderTitle";
import Pagination from "components/Pagination";
import Button from "components/Button";

type ProjectViewType = 'grid'|'list';

const COLOR_ACTIVE = '#465C6F';
const COLOR_INACTIVE = '#A1B3C1';

export const PROJECTS_TITLE = "DataPipeline Projects";

/*
 * Note on the name: The PoC version was called ProjectsView and after I got the
 * design the new element got the name BeautifulProjectsView
 */

const sortByOptions: SelectOption<string>[] = [
  { name: "Last run first", value: "last_run_first" },
  { name: "Last run last", value: "last_run_last" },
  { name: "Last created first", value: "last_created_first" },
  { name: "Last created last", value: "last_created_last" },
  { name: "Scheduled first", value: "scheduled_first" },
  { name: "Scheduled last", value: "scheduled_last" },
];

export default function BeautifulProjectsView() {
  const { projects, inProgress, totalCount, page, setPage, orderBy, setOrderBy } = useHostedScrapingProjects();
  const [ view, setView ] = useLocalStorage<ProjectViewType>('ProjectsViewLayout','list');

  const sortBySelectOption = useMemo(() => {
    return sortByOptions.find(opt => opt.value === orderBy);
  }, [ orderBy ]);

  const hasNextPage = (page + 1) * PROJECT_PAGE_SIZE < totalCount;

  if (inProgress && projects.length === 0) {
    return (<FullWidthLoadingSpinner />);
  }

  const projectItems =
  (view === 'grid' && projects.length > 0) ?
    (<ProjectGridView projects={projects} />)
  : (view === 'list' && projects.length > 0) ?
    (<ProjectListView projects={projects} />)
  : (view === 'grid' && projects.length === 0) ?
    (<EmptyProjectGridView/>)
  : (view === 'list' && projects.length === 0) ?
    (<EmptyProjectListView/>)
  : null;


  // TODO: The background should be bg-neutral-50 (249, 250, 251) but it's way darker than it should be
  return (
    <>
    <ScrollToHeaderTitle/>
    <BorderedPage className="bg-slate-50" title={PROJECTS_TITLE}>
      <div className="flex flex-col items-start gap-5 md:px-14 pt-10 pb-24">
        <div className="w-full flex flex-wrap justify-end flex-row align-center items-center gap-2">
          <div>All projects ({totalCount})</div>
          <div className="grow"></div>
          <div className="flex flex-row items-center">
            <LabelledSelect
              label="Sort by"
              className="mr-4 w-60"
              value={ sortBySelectOption || sortByOptions[0] }
              options={ sortByOptions }
              onChange={ (newValue: SelectOption<string>) => {
                setOrderBy(newValue.value);
                setPage(0);
              } }
            />
            <div onClick={() => setView('grid')}>
              <ProjectGridIcon className="m-2 cursor-pointer" fill={view === 'grid' ? COLOR_ACTIVE : COLOR_INACTIVE}/>
            </div>
            <div onClick={() => setView('list')}>
              <ProjectListIcon className="m-2 cursor-pointer" fill={view === 'list' ? COLOR_ACTIVE : COLOR_INACTIVE}/>
            </div>
          </div>
          <Button text="NEW PROJECT"
            icon={ { Icon: PlusCircleIcon } }
            href="/projects/new"
            className="bg-brandPrimary hover:brandPrimary-700 text-white rounded-md font-medium"
            data-testid="new-project-button"
          />
        </div>
        {inProgress ? <FullWidthLoadingSpinner /> : projectItems}
        {totalCount > PROJECT_PAGE_SIZE && totalCount > projects.length && (
          <div className="w-full">
            <Pagination
              currentPage={page + 1}
              hasNextPage={hasNextPage}
              onPageChange={(newPage) => setPage(newPage - 1)}
            />
          </div>
        )}
      </div>
    </BorderedPage>
    </>
  );

}
